'use es6';

import { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TRUNCATED_TOOLTIP_CHARACTER_LIMIT, SEARCH_BAR_HEIGHT, CONTENTS_TAB_ACTION } from './constants';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { getModuleIcon, getDefaultType } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { getIsAdvancedModuleWithoutAccess, getModuleHasPaymentsFieldWithoutAccess, getModuleHasPersonalizationTokensWithoutAccess } from 'ContentEditorUI/redux/selectors/downgradeSelectors';
import { makeGetSchemaForModule } from 'ContentEditorUI/redux/selectors/moduleSchemaSelectors';
import { getHasSmartContentScope } from 'ContentEditorUI/redux/selectors/authSelectors';
import { isSmartEnabled } from 'ContentEditorUI/lib/smartContent/utils';
import { getModuleHasErrors } from 'ContentEditorUI/redux/selectors/moduleErrorSelectors';
import { setBreakpointStylesForTreeNode } from 'ContentEditorUI/redux/actions/moduleActions';
import { getActiveBreakpointName } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import { closeTreeNodes, openTreeNodes } from 'ContentEditorUI/redux/actions/treeViewSidebarActions';
import { getSidebarScrollTop, getActiveMatch } from 'ContentEditorUI/redux/selectors/treeViewSidebarSelectors';
export const i18nKey = key => `sidebar.moduleListItem.${key}`;
export const trackAction = eventName => {
  UsageTracker.trackEditorInteraction({
    action: CONTENTS_TAB_ACTION,
    event: eventName
  });
};
export const getIsGlobalGroupOrPartial = module => ['global_group', 'global_partial'].includes(module.get('type'));
export const getIsSection = layoutTreeData => layoutTreeData.getParentName() === layoutTreeData.getRootName();
export const getUniqueModuleId = module => {
  const isGlobalGroupOrPartial = getIsGlobalGroupOrPartial(module);
  return module.get(isGlobalGroupOrPartial ? 'path' : 'name');
};

// Calculate which label to display for the module, and format it
export const useModuleLabel = (matchableValues = [], nodeName) => {
  var _matchableValues$node, _nameObject$textConte;
  const nameObject = (_matchableValues$node = matchableValues[nodeName]) === null || _matchableValues$node === void 0 ? void 0 : _matchableValues$node.names;
  const name = (nameObject === null || nameObject === void 0 || (_nameObject$textConte = nameObject.textContents) === null || _nameObject$textConte === void 0 ? void 0 : _nameObject$textConte.length) > 0 ? nameObject === null || nameObject === void 0 ? void 0 : nameObject.textContents : nameObject === null || nameObject === void 0 ? void 0 : nameObject.moduleDisplayLabel;
  if (name) {
    const showEllipsis = name.length > TRUNCATED_TOOLTIP_CHARACTER_LIMIT;
    const truncatedName = name.substring(0, TRUNCATED_TOOLTIP_CHARACTER_LIMIT);
    return `${truncatedName}${showEllipsis ? '...' : ''}`;
  }
  return '';
};

// Determine if a module is a global module
export const useIsGlobalModule = module => {
  const isGlobalGroupOrPartial = getIsGlobalGroupOrPartial(module);
  const getSchemaForModule = useMemo(makeGetSchemaForModule, []);
  const moduleSpec = useSelector(state => getSchemaForModule(state, module));
  return module.get('type') === 'global_widget' || isGlobalGroupOrPartial || moduleSpec && moduleSpec.global;
};

// Determine if a module has smart rules
export const useShowSmartRulesIcon = (isGlobalModule, module) => {
  const uniqueModuleId = getUniqueModuleId(module);
  const canUseSmartRules = useSelector(getHasSmartContentScope);
  const defaultModuleType = useSelector(state => getDefaultType(state, uniqueModuleId));
  return !isGlobalModule && canUseSmartRules && isSmartEnabled(defaultModuleType, module);
};

// Determine if a module has validation errors
export const useHasModuleErrors = module => {
  const hasModuleErrors = useSelector(state => getModuleHasErrors(state, module));
  const isAdvancedModuleWithoutAccess = useSelector(state => getIsAdvancedModuleWithoutAccess(state, module));
  const moduleHasPaymentFieldWithoutAccess = useSelector(state => getModuleHasPaymentsFieldWithoutAccess(state, module));
  const hasPersonalizationTokenWithoutAccess = useSelector(state => getModuleHasPersonalizationTokensWithoutAccess(state, module));
  return !!(hasModuleErrors || isAdvancedModuleWithoutAccess || moduleHasPaymentFieldWithoutAccess || hasPersonalizationTokenWithoutAccess);
};

// Determine the icon path prop for the module
export const useIconPath = module => {
  const isGlobalGroupOrPartial = getIsGlobalGroupOrPartial(module);
  const uniqueModuleId = module.get(isGlobalGroupOrPartial ? 'path' : 'name');
  const getSchemaForModule = useMemo(makeGetSchemaForModule, []);
  const moduleSpec = useSelector(state => getSchemaForModule(state, module));
  const iconForModulesWithId = useSelector(state => getModuleIcon(state, uniqueModuleId));
  return iconForModulesWithId || moduleSpec && moduleSpec.icon;
};
export const useToggleNodeVisibilityOnThisBreakpoint = layoutTreeData => {
  const dispatch = useDispatch();
  const activeBreakpointName = useSelector(getActiveBreakpointName);
  return useCallback((isHidden, e) => {
    if (e) {
      e.stopPropagation();
    }
    const newStyles = {
      hidden: !isHidden
    };
    dispatch(setBreakpointStylesForTreeNode(layoutTreeData.getRootName(), layoutTreeData.getName(), newStyles, activeBreakpointName));
  }, [activeBreakpointName, dispatch, layoutTreeData]);
};
export const getRowById = (tree, id) => {
  if (tree && tree.hasRow(id)) {
    return tree.findRow(id);
  }
  return null;
};
const NODE_SEARCH_DATA_ATTR = 'data-node-search-name';
export const getNodeSearchClass = name => `sidebar-search-result-${name}`;
export const getNodeSearchDataProp = name => ({
  [NODE_SEARCH_DATA_ATTR]: getNodeSearchClass(name)
});
export const getSidebarScrollToForMatch = name => {
  const $sidebar = document.getElementsByClassName('sidebar-scrollable-tab-panel')[0];
  if ($sidebar) {
    const nameToSearch = getNodeSearchClass(name);
    const $matchElement = document.querySelectorAll(`[${NODE_SEARCH_DATA_ATTR}="${nameToSearch}"]`)[0];
    if ($matchElement) {
      const sidebarBoundingRect = $sidebar.getBoundingClientRect();
      const sidebarTop = sidebarBoundingRect.top;
      const sidebarBottom = sidebarBoundingRect.bottom;
      const sidebarMid = (sidebarTop + sidebarBottom) / 2;
      const matchBoundingRect = $matchElement.getBoundingClientRect();
      const matchRectTop = matchBoundingRect.top;
      const isInViewport = matchRectTop >= sidebarTop && matchRectTop <= sidebarBottom;
      if (!isInViewport) {
        const pixelDiffToMid = matchRectTop - sidebarMid;
        const newSidebarScrollTop = $sidebar.scrollTop + pixelDiffToMid;
        return newSidebarScrollTop;
      }
    }
  }
  return null;
};
export const getSidebarScrollTopToSave = () => {
  const $sidebar = document.getElementsByClassName('sidebar-scrollable-tab-panel')[0];
  if ($sidebar && $sidebar.length) {
    const currentScrollTop = $sidebar.scrollTop;
    const adjustedScrollTop = currentScrollTop - SEARCH_BAR_HEIGHT;
    return adjustedScrollTop;
  }
  return 0;
};
export const getAllSectionNames = (isDndArea, layoutTreeData) => {
  let children = [];
  if (isDndArea) {
    children = layoutTreeData.isCell() ? layoutTreeData.getRows() : layoutTreeData.getColumns();
  }
  return children.map(child => child.getName());
};
export const useGetCollapseAllSectionsFunction = (isDndArea, layoutTreeData) => {
  const dispatch = useDispatch();
  return useCallback(e => {
    e.stopPropagation();
    UsageTracker.trackEditorInteraction({
      action: CONTENTS_TAB_ACTION,
      event: 'Collapse All Sections'
    });
    const sectionNames = getAllSectionNames(isDndArea, layoutTreeData);
    dispatch(closeTreeNodes(sectionNames));
  }, [dispatch, isDndArea, layoutTreeData]);
};
export const useGetExpandAllSectionsFunction = (isDndArea, layoutTreeData) => {
  const dispatch = useDispatch();
  return useCallback(e => {
    e.stopPropagation();
    UsageTracker.trackEditorInteraction({
      action: CONTENTS_TAB_ACTION,
      event: 'Expand All Sections'
    });
    const sectionNames = getAllSectionNames(isDndArea, layoutTreeData);
    dispatch(openTreeNodes(sectionNames));
  }, [dispatch, isDndArea, layoutTreeData]);
};
export const getVisibilityLangKey = ({
  activeBreakpointName,
  isHidden
}) => {
  if (activeBreakpointName === 'mobile') {
    if (isHidden) {
      return 'components.sidebar.treeView.expandableMenu.mobile.show';
    }
    return 'components.sidebar.treeView.expandableMenu.mobile.hide';
  }
  if (isHidden) {
    return 'components.sidebar.treeView.expandableMenu.default.show';
  }
  return 'components.sidebar.treeView.expandableMenu.default.hide';
};
export const scrollToPositionInSidebar = scrollPos => {
  const $sidebar = document.getElementsByClassName('sidebar-scrollable-tab-panel')[0];
  if ($sidebar) {
    window.requestAnimationFrame(() => {
      $sidebar.scrollTop = scrollPos;
    });
  }
};
export const updateSidebarScrollTopOnMountWithReduxState = () => {
  const reduxState = window.hubspot.ContentEditorUI.store.getState();
  const scrollTopFromRedux = getSidebarScrollTop(reduxState);
  const sidebarActiveMatch = getActiveMatch(reduxState);
  if (scrollTopFromRedux > 0 && sidebarActiveMatch && sidebarActiveMatch !== '') {
    scrollToPositionInSidebar(scrollTopFromRedux);
  }
};